/**
 * The single description of every app-user filter.
 *
 * Both sides read this: the web renders its chip row from
 * `USER_FILTER_CATALOG ∩ APP_REGISTRY[app].supportedUserFilters`, and the API
 * validates an incoming filter bag against the same descriptors. Adding a
 * filter means adding one entry here, one adapter clause, and one i18n label.
 */

export type UserFilterViewId = 'default' | 'economy' | 'moderation';

export type UserFilterKind = 'dateWindow' | 'enumMulti' | 'numberRange' | 'bool';

export const USER_FILTER_KEYS = [
  // default view
  'registered', 'lastLogin', 'accountState', 'accountType',
  'gender', 'age', 'photoState', 'hasUsername',
  'platform', 'appVersion', 'pushReachable',
  // economy view
  'coinBalance', 'lifetimeCredited', 'lifetimeDebited', 'lastTransaction',
  'vipState', 'vipProduct', 'payerState', 'store', 'rcLinked',
  // moderation view
  'banState', 'banReason', 'restriction', 'syntheticBatch',
] as const;
export type UserFilterKey = (typeof USER_FILTER_KEYS)[number];

export interface UserFilterDescriptor {
  readonly key: UserFilterKey;
  readonly kind: UserFilterKind;
  /**
   * Views whose chip row renders this filter. A key may appear in more than
   * one view; per-view starting values live in the view definition
   * (`_views/built-in-views.ts`), never here.
   */
  readonly views: readonly UserFilterViewId[];
  /**
   * enumMulti only. A fixed option set, or `'dynamic'` when options are
   * discovered from the app's own data via GET /app-users/:app/filter-options.
   */
  readonly options?: readonly string[] | 'dynamic';
  /** dateWindow only. Offer a "Never" bucket matching rows whose column IS NULL. */
  readonly nullBucket?: boolean;
  /** Key that must also be set for this filter to mean anything. */
  readonly requires?: UserFilterKey;
}

function d(desc: UserFilterDescriptor): UserFilterDescriptor {
  return Object.freeze(desc);
}

export const USER_FILTER_CATALOG: Readonly<Record<UserFilterKey, UserFilterDescriptor>> =
  Object.freeze({
    // ── default view ────────────────────────────────────────────────────
    registered: d({ key: 'registered', kind: 'dateWindow', views: ['default'] }),
    lastLogin: d({ key: 'lastLogin', kind: 'dateWindow', views: ['default'], nullBucket: true }),
    accountState: d({
      key: 'accountState', kind: 'enumMulti', views: ['default'],
      options: ['active', 'blocked', 'deleted'],
    }),
    accountType: d({
      key: 'accountType', kind: 'enumMulti', views: ['default'],
      options: ['human', 'synthetic', 'dev', 'liveop', 'bot'],
    }),
    gender: d({
      key: 'gender', kind: 'enumMulti', views: ['default'],
      options: ['male', 'female', 'other', 'unset'],
    }),
    age: d({ key: 'age', kind: 'numberRange', views: ['default', 'moderation'] }),
    photoState: d({
      key: 'photoState', kind: 'enumMulti', views: ['default', 'moderation'],
      options: ['none', 'pending', 'approved', 'rejected'],
    }),
    hasUsername: d({ key: 'hasUsername', kind: 'bool', views: ['default'] }),
    platform: d({ key: 'platform', kind: 'enumMulti', views: ['default'], options: 'dynamic' }),
    appVersion: d({ key: 'appVersion', kind: 'enumMulti', views: ['default'], options: 'dynamic' }),
    pushReachable: d({ key: 'pushReachable', kind: 'bool', views: ['default'] }),

    // ── economy view ────────────────────────────────────────────────────
    coinBalance: d({ key: 'coinBalance', kind: 'numberRange', views: ['economy'] }),
    lifetimeCredited: d({ key: 'lifetimeCredited', kind: 'numberRange', views: ['economy'] }),
    lifetimeDebited: d({ key: 'lifetimeDebited', kind: 'numberRange', views: ['economy'] }),
    lastTransaction: d({
      key: 'lastTransaction', kind: 'dateWindow', views: ['economy'], nullBucket: true,
    }),
    vipState: d({
      key: 'vipState', kind: 'enumMulti', views: ['economy'],
      options: ['active', 'expiring', 'expired'],
    }),
    vipProduct: d({
      key: 'vipProduct', kind: 'enumMulti', views: ['economy'],
      options: 'dynamic', requires: 'vipState',
    }),
    payerState: d({
      key: 'payerState', kind: 'enumMulti', views: ['economy'],
      options: ['never', 'activeSub', 'lapsed', 'consumableOnly'],
    }),
    store: d({ key: 'store', kind: 'enumMulti', views: ['economy'], options: 'dynamic' }),
    rcLinked: d({ key: 'rcLinked', kind: 'bool', views: ['economy'] }),

    // ── moderation view ─────────────────────────────────────────────────
    banState: d({
      key: 'banState', kind: 'enumMulti', views: ['moderation'],
      options: ['none', 'active', 'expired', 'revoked'],
    }),
    banReason: d({ key: 'banReason', kind: 'enumMulti', views: ['moderation'], options: 'dynamic' }),
    restriction: d({
      key: 'restriction', kind: 'enumMulti', views: ['moderation'],
      // No `anyActive`: it would overlap the capabilities and break the
      // invariant that selecting every option equals no filter. Every
      // capability selected already means "any active restriction"; `none`
      // completes the set.
      //
      // All five capabilities of the `restrictions.capability` ENUM, verified
      // against `shuffly/database/init/01_schema.sql` and
      // `app/Support/ModerationCapability.php`. `no_game_invite` is enforced
      // (GameInvitationService) — omitting it would both hide a real filter and
      // break the exhaustiveness rule, since selecting "everything" would then
      // still have excluded users restricted only from game invites.
      options: [
        'none', 'no_voice_room', 'no_room_speak', 'no_call', 'no_dm', 'no_game_invite',
      ],
    }),
    syntheticBatch: d({
      key: 'syntheticBatch', kind: 'enumMulti', views: ['moderation'], options: 'dynamic',
    }),
  });

/**
 * Sort columns the adapter will honour. Never widened from operator input.
 *
 * `coin_balance` resolves to `user_wallet_summary.balance`, never the legacy
 * `users.coin_balance` denormalization (design Decision 9) — sorting on the
 * stale column while the economy filters read the authoritative one would let
 * a row sort out of position relative to the balance shown next to it.
 */
export const USER_SORT_KEYS = [
  'created_at', 'last_login_at', 'total_calls', 'coin_balance', 'id',
] as const;
export type UserSortKey = (typeof USER_SORT_KEYS)[number];

// ── value shapes ──────────────────────────────────────────────────────

export interface DateWindowValue {
  /** Inclusive lower bound, absolute ISO 8601 instant. */
  readonly from?: string;
  /** Exclusive upper bound, absolute ISO 8601 instant. */
  readonly to?: string;
  /** Also match rows whose column IS NULL. ORs with the range. */
  readonly includeNull?: boolean;
}
export interface NumberRangeValue {
  readonly min?: number;
  readonly max?: number;
}
export type EnumMultiValue = readonly string[];
export type BoolValue = boolean;

export type UserFilterValue =
  | DateWindowValue | NumberRangeValue | EnumMultiValue | BoolValue;

export type UserFilterValues = Partial<Record<UserFilterKey, UserFilterValue>>;

// ── guards ────────────────────────────────────────────────────────────

export function isUserFilterKey(value: string): value is UserFilterKey {
  return (USER_FILTER_KEYS as readonly string[]).includes(value);
}

export function filtersForView(view: UserFilterViewId): readonly UserFilterDescriptor[] {
  return USER_FILTER_KEYS
    .map((k) => USER_FILTER_CATALOG[k])
    .filter((desc) => desc.views.includes(view));
}
